<script setup lang="ts">
import { computed, ref } from 'vue'

import { PopoverMenu, PopoverMenuOption } from '@collector/shared-ui'
import {
  getParticipantTeamSide,
  ParticipantWithLogo,
} from '@collector/shared-ui-domain'
import {
  CommonStatId,
  Participant,
  SoccerEventDetailId,
} from '@collector/sportsapi-client-legacy'
import {
  useEventDetails,
  useParticipantStatsMaps,
} from '@desktop/views/Relation/composables'

import { ModalOptions } from '../types'
import FormationsButton from './Formations/FormationsButton.vue'
import LineupsButton from './Lineups/LineupsButton.vue'
import SkinsButton from './Skins/SkinsButton.vue'
import SkinsModal from './Skins/SkinsModal.vue'

interface Props {
  participant: Participant
  modalOptions: ModalOptions
  reversed?: boolean
}

const props = defineProps<Props>()

const { getEventDetailValue } = useEventDetails()
const { homeParticipantStatsMap, awayParticipantStatsMap } =
  useParticipantStatsMaps()

const participantStatsMap =
  getParticipantTeamSide(props.participant) === 'home'
    ? homeParticipantStatsMap
    : awayParticipantStatsMap

const isSkinsModalVisible = ref(false)
const isLineupsModalVisible = ref(false)
const isFormationsModalVisible = ref(false)

function onClickLineupOption(): void {
  isLineupsModalVisible.value = true
}

function onClickSkinsOption(): void {
  isSkinsModalVisible.value = true
}

function onClickFormationsOption(): void {
  isFormationsModalVisible.value = true
}

const isFormationsDisabled = computed(
  () => getEventDetailValue(SoccerEventDetailId.HasFormations) === 'no', // TODO: remove SoccerEventDetailId dependency
)

const isSkinsDisabled = computed(() => {
  const skinPatternStat = participantStatsMap.value[CommonStatId.SkinPattern]
  return skinPatternStat === undefined || skinPatternStat === null
})

const popoverMenuOptions = computed(() => {
  const options: PopoverMenuOption[] = []

  if (props.modalOptions.lineups) {
    options.push({ name: 'lineups', onClick: onClickLineupOption })
  }
  if (props.modalOptions.skins) {
    options.push({ name: 'skins', onClick: onClickSkinsOption })
  }
  if (props.modalOptions.formations) {
    options.push({
      name: 'formations',
      disabled: isFormationsDisabled.value,
      onClick: onClickFormationsOption,
    })
  }

  return options
})
</script>

<template>
  <div
    class="flex justify-between"
    :class="{
      'flex-row-reverse': reversed,
    }"
  >
    <div class="@lg:hidden">
      <PopoverMenu
        :content="{
          align: reversed ? 'end' : 'start',
        }"
        :options="popoverMenuOptions"
        optionClass="capitalize"
      />
    </div>
    <div
      class="hidden gap-x-2 @lg:flex"
      :class="{
        'flex-row-reverse': !reversed,
      }"
    >
      <LineupsButton
        v-if="modalOptions.lineups"
        @click="onClickLineupOption"
      />
      <SkinsButton
        v-if="modalOptions.skins"
        :disabled="isSkinsDisabled"
        @click="onClickSkinsOption"
      />
      <FormationsButton
        v-if="modalOptions.formations"
        :disabled="isFormationsDisabled"
        @click="onClickFormationsOption"
      />
    </div>

    <ParticipantWithLogo
      :participant
      :reversed
    />
  </div>
  <Component
    :is="modalOptions.lineups"
    v-if="isLineupsModalVisible"
    :participant
    @close="isLineupsModalVisible = false"
  />
  <Component
    :is="modalOptions.formations"
    v-if="isFormationsModalVisible"
    :participant
    @close="isFormationsModalVisible = false"
  />
  <SkinsModal
    v-if="isSkinsModalVisible"
    :participant
    @close="isSkinsModalVisible = false"
  />
</template>
